import { SourceContinuityTransport, type SourceContinuity, type SourceLane } from './sourceContinuity';

export interface SourceObservation {
  lane?: unknown; sourceEpoch?: unknown; sourceGen?: unknown;
  observedAtMs?: unknown; snapshotMs?: unknown; pendingResync?: unknown;
  source?: unknown; sport?: unknown;
  /** Raw wire sport key of a row the tab folded to its base sport (ALT
   *  pseudo-sports); the engine's lane suffix names THIS, not `sport`. */
  feedSport?: unknown;
}
export interface SourceProof {
  feed: string;
  session: object;
  kind: 'market' | 'orderbook';
  announced: boolean;
  row: SourceObservation;
}
export type SourceVerdict = 'continuous' | 'unconfirmed';
const positive = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v) && v > 0;

/** Authority belongs to a configured feed AND its current transport session.
 * Row timestamps are producer observations, never subtracted from browser time.
 * Quiet rows remain valid under their exact governing lane generation.
 */
export class FeedSourceAuthority {
  private feeds = new Map<string, { session: object; controls: SourceContinuityTransport }>();
  private resyncSince = new WeakMap<object, Map<SourceLane, number>>();
  require(feed: string): void {
    this.feeds.set(feed, { session: {}, controls: new SourceContinuityTransport() });
  }
  required(feed: string | undefined): boolean { return feed != null && this.feeds.has(feed); }
  /** The current session's lane controls for `feed` — what the engine itself
   *  says about each lane it serves (empty for a feed that requires no
   *  evidence, and after a reconnect until the new session's controls land). */
  lanes(feed: string): readonly SourceContinuity[] {
    return this.feeds.get(feed)?.controls.snapshot() ?? [];
  }
  retain(feeds: ReadonlySet<string>): void {
    for (const feed of this.feeds.keys()) if (!feeds.has(feed)) this.feeds.delete(feed);
  }
  control(feed: string, frame: SourceContinuity | null, invalidLane?: SourceLane): void {
    const state = this.feeds.get(feed);
    if (!state) return;
    if (frame === null && invalidLane) state.controls.quarantine(invalidLane);
    else if (frame === null) { state.session = {}; state.controls.reset(); }
    else {
      const previous = state.controls.get(frame.lane);
      if (state.controls.receive(frame) !== 'accepted') return;
      let starts = this.resyncSince.get(state.session);
      if (!starts) { starts = new Map(); this.resyncSince.set(state.session, starts); }
      if (frame.state !== 'resyncing') starts.delete(frame.lane);
      else if (previous?.state === 'valid' && previous.sourceGen === frame.sourceGen
        && previous.sourceEpoch === frame.sourceEpoch) starts.set(frame.lane, performance.now());
    }
  }
  /** Display-only grace for an already confirmed book in the exact same
   * session/generation. This never changes verdict() or trading eligibility.
   * Genuine gaps, quarantine, reconnect and generation changes get no grace. */
  displayResyncDeadline(proof: SourceProof): number | undefined {
    const state = this.feeds.get(proof.feed);
    if (!state || state.session !== proof.session || !proof.announced) return;
    const lane = proof.row.lane as SourceLane;
    const control = state.controls.get(lane);
    const since = this.resyncSince.get(state.session)?.get(lane);
    if (control?.state !== 'resyncing' || control.sourceEpoch !== proof.row.sourceEpoch
      || control.sourceGen !== proof.row.sourceGen || since === undefined) return;
    return since + 2_000;
  }
  capture(feed: string | undefined, kind: SourceProof['kind'], row?: SourceObservation): SourceProof | undefined {
    const state = feed == null ? undefined : this.feeds.get(feed);
    const governing = state?.controls.get(row?.lane as SourceLane);
    return state && feed != null ? { feed, session: state.session, kind,
      announced: governing != null && governing.sourceEpoch === row?.sourceEpoch && governing.sourceGen === row?.sourceGen,
      row: {
      lane: row?.lane, sourceEpoch: row?.sourceEpoch, sourceGen: row?.sourceGen,
      observedAtMs: row?.observedAtMs, snapshotMs: row?.snapshotMs, pendingResync: row?.pendingResync,
      source: row?.source, sport: row?.sport, feedSport: row?.feedSport,
    } } : undefined;
  }
  verdict(proof: SourceProof | undefined): SourceVerdict | undefined {
    if (!proof) return undefined; // explicitly legacy or synthetic caller
    const state = this.feeds.get(proof.feed), row = proof.row;
    if (!state || state.session !== proof.session || !proof.announced || !positive(row.observedAtMs)
      || typeof row.sourceEpoch !== 'string' || !Number.isSafeInteger(row.sourceGen)
      || (row.sourceGen as number) < 0 || typeof row.lane !== 'string') return 'unconfirmed';
    const usLane = row.lane === 'polyus-floor' || row.lane.startsWith('polyus-book:');
    const bookLane = row.lane === 'kalshi-orderbook' || row.lane.startsWith('kalshi-orderbook:')
      || row.lane.startsWith('poly-book:') || row.lane.startsWith('polyus-book:');
    const marketLane = row.lane === 'kalshi-ticker' || row.lane === 'poly-floor'
      || row.lane.startsWith('kalshi-ticker:') || row.lane.startsWith('poly-book:') || usLane;
    // Partial Poly shard drops keep the lane valid. Both market and book
    // frames withdraw only their affected rows until the snapshot clears it.
    if (row.pendingResync !== undefined && row.pendingResync !== false) return 'unconfirmed';
    if (proof.kind === 'market' ? !marketLane : (!bookLane || !positive(row.snapshotMs)
      || row.snapshotMs > row.observedAtMs)) return 'unconfirmed';
    // US continuity is a separate venue authority. A mislabeled Kalshi/Poly
    // lane must never certify US, nor may US controls certify an unknown venue.
    if ((row.source === 'polymarket_us') !== usLane
      || row.source === 'kalshi' && !row.lane.startsWith('kalshi-')
      || row.source === 'polymarket' && !row.lane.startsWith('poly-')) return 'unconfirmed';
    const suffix = row.lane.split(':')[1];
    // The lane is keyed by the engine's FEED key (`kalshi-ticker:NFLALT` on an
    // ALT edge); a row folded to its base sport for display still proves
    // itself on the raw key it arrived under.
    const laneSport = typeof row.feedSport === 'string' ? row.feedSport : row.sport;
    if (suffix && typeof laneSport === 'string' && suffix !== laneSport.toUpperCase()) return 'unconfirmed';
    const governing = state.controls.get(row.lane as SourceLane);
    return governing?.state === 'valid' && governing.sourceEpoch === row.sourceEpoch
      && governing.sourceGen === row.sourceGen ? 'continuous' : 'unconfirmed';
  }
  /** Queued old-generation data may follow its successor's announcement.
   * Keep it for initial display only; never overwrite a row already observed
   * under the current authority with a retired generation's value. */
  supersedes(prior: SourceProof | undefined, next: SourceProof | undefined): boolean {
    if (prior && next && prior.feed === next.feed && prior.session === next.session) {
      const control = this.feeds.get(next.feed)?.controls.get(next.row.lane as SourceLane);
      const priorControl = this.feeds.get(prior.feed)?.controls.get(prior.row.lane as SourceLane);
      // A same-generation maintenance pause withdraws execution authority,
      // not the observation itself. An obsolete ticker replay must not erase
      // a current book's proof while it waits for its control to recover.
      // Compare each proof with its OWN lane: ticker/book generations differ.
      const priorIsCurrent = prior.announced && priorControl?.sourceEpoch === prior.row.sourceEpoch
        && priorControl?.sourceGen === prior.row.sourceGen;
      if (control && typeof next.row.sourceEpoch === 'string' && Number.isSafeInteger(next.row.sourceGen)
        && (next.row.sourceEpoch !== control.sourceEpoch || (next.row.sourceGen as number) < control.sourceGen)
        && priorIsCurrent) return false;
    }
    if (!prior || !next || prior.feed !== next.feed || prior.session !== next.session
      || prior.row.lane !== next.row.lane) return true;
    if (prior.row.sourceEpoch !== next.row.sourceEpoch) return this.verdict(prior) !== 'continuous';
    return !(typeof prior.row.sourceGen === 'number' && typeof next.row.sourceGen === 'number'
      && next.row.sourceGen < prior.row.sourceGen);
  }
}

/** Negotiation is per connection; preserve every other subscription field. */
export function withSourceEvidence<T extends Record<string, unknown>>(msg: T, enabled: boolean): T {
  return enabled && msg.op === 'subscribe' && ['markets','orderbooks','all'].includes(msg.channel as string)
    ? { ...msg, evidence: 1 } : msg;
}
